import React, { useMemo, useState, memo } from "react";
import { Avatar, Box, Divider, Menu, MenuItem, Tooltip } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { styled } from "@mui/system";
import { useAssignees } from "./AssigneeContext";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  gap: "8px",
  padding: "6px 12px",
  "&.Mui-selected": {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
    },
  },
  "&:hover": {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
}));

const SmallAvatar = styled(Avatar)(({ theme }) => ({
  width: 22,
  height: 22,
  fontSize: "0.7rem",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

// Compact, tag-like trigger. Reads as a chip rather than a button.
const Pill = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  maxWidth: "100%",
  height: 26,
  padding: "0 8px 0 3px",
  borderRadius: 13,
  cursor: "pointer",
  backgroundColor: theme.palette.action.hover,
  color: theme.palette.text.primary,
  fontSize: "0.78rem",
  lineHeight: 1,
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
}));

// Unassigned state: neutral grey placeholder, no "error-like" dashed circle.
const UnassignedPlaceholder = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 26,
  height: 26,
  borderRadius: "50%",
  cursor: "pointer",
  color: theme.palette.text.disabled,
  backgroundColor: theme.palette.action.hover,
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
}));

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const displayName = (assignee) => assignee?.full_name || assignee?.username || "";

/**
 * Picks the assignee (or reviewer) for a single row/field.
 *
 * The rendered value is derived from `currentSelected` on every render rather
 * than mirrored into local state: local state used to go stale (the pill showed
 * the id, not the person, right after a pick) and to render blank when the
 * caller supplied an id that had not yet been matched against the roster.
 *
 * `currentSelected` may be a user id (number *or* the string ids that come out
 * of localStorage / form state) or a nested user object, since the list and
 * detail serializers disagree on which they send.
 *
 * This control never touches the app-wide "whose tasks am I looking at"
 * selection — that lives in the header's own picker. Writing to it from here
 * meant assigning a task silently re-filtered every list on the page.
 */
const AssigneeDropdown = memo(
  ({ currentSelected, onChange, compact = false, disabled = false, allowUnassign = true }) => {
    const { assignees } = useAssignees();
    const [anchorEl, setAnchorEl] = useState(null);

    // Resolve whatever the caller handed us against the roster. Ids are compared
    // as strings so a numeric id and its string form both match.
    const selected = useMemo(() => {
      if (currentSelected == null || currentSelected === "") return null;
      if (typeof currentSelected === "object") {
        // Prefer the roster entry so the avatar/name are complete even when the
        // caller's nested object only carries a subset of the user fields.
        return (
          assignees.find((a) => String(a.id) === String(currentSelected.id)) ||
          currentSelected
        );
      }
      return (
        assignees.find((a) => String(a.id) === String(currentSelected)) || null
      );
    }, [currentSelected, assignees]);

    const handleAssigneeSelect = (userId) => {
      setAnchorEl(null);
      onChange(userId);
    };

    const handleClick = (event) => {
      event.stopPropagation();
      if (disabled) return;
      setAnchorEl(event.currentTarget);
    };

    const renderTrigger = () => {
      if (!selected) {
        return (
          <Tooltip title="Unassigned" arrow>
            <UnassignedPlaceholder onClick={handleClick}>
              <PersonOutlineIcon sx={{ fontSize: 16 }} />
            </UnassignedPlaceholder>
          </Tooltip>
        );
      }

      const name = displayName(selected);
      const initials = getInitials(name);

      // Dense contexts (Kanban cards): avatar only.
      if (compact) {
        return (
          <Tooltip title={name} arrow>
            <SmallAvatar
              onClick={handleClick}
              src={`/media/user_${selected.id}/avatar`}
              alt={name}
            >
              {initials}
            </SmallAvatar>
          </Tooltip>
        );
      }

      return (
        <Pill onClick={handleClick}>
          <SmallAvatar src={`/media/user_${selected.id}/avatar`} alt={name}>
            {initials}
          </SmallAvatar>
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </Box>
        </Pill>
      );
    };

    return (
      <div className="assignee-component">
        {renderTrigger()}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          {assignees.map((assignee) => (
            <StyledMenuItem
              key={assignee.id}
              onClick={() => handleAssigneeSelect(assignee.id)}
              selected={String(assignee.id) === String(selected?.id)}
            >
              <SmallAvatar
                src={`/media/user_${assignee.id}/avatar`}
                alt={displayName(assignee)}
                style={{ marginRight: "8px" }}
              >
                {getInitials(displayName(assignee))}
              </SmallAvatar>
              <span>{displayName(assignee)}</span>
            </StyledMenuItem>
          ))}
          {allowUnassign && selected && <Divider />}
          {allowUnassign && selected && (
            <StyledMenuItem onClick={() => handleAssigneeSelect(null)}>
              <UnassignedPlaceholder
                as="span"
                sx={{ width: 22, height: 22, mr: 1 }}
              >
                <PersonOutlineIcon sx={{ fontSize: 14 }} />
              </UnassignedPlaceholder>
              <span>Unassigned</span>
            </StyledMenuItem>
          )}
        </Menu>
      </div>
    );
  }
);

AssigneeDropdown.displayName = "AssigneeDropdown";

export default AssigneeDropdown;
